// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';
import {RoleDescriptionsContainer} from './RoleDescription';

const cardholderRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.cardholderRole.roleName',
    description: 'The name of the cardholder role.',
    defaultMessage: 'Cardholder',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.cardholderRole.shortDescription',
    description: 'The short description of the cardholder role.',
    defaultMessage: 'Can view card transactions and replace card',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.cardholderRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the cardholder role can do.',
    defaultMessage:
      'This role is for employees who have a Stripe corporate card. They can only view their own card information.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.cardholderRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the cardholder role can't do.",
    defaultMessage:
      "They can't view company-wide corporate card data or reports, payments, or any account settings.",
  },
});

const corporateCardRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...cardholderRoleMessages.roleName} />,
  roleId: 'cardholder',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...cardholderRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...cardholderRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...cardholderRoleMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.viewTheirTransactions} />,
    <FormattedMessage {...actionsRolesAllow.freezeTheirCard} />,
    <FormattedMessage {...actionsRolesAllow.replaceTheirCard} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
    <FormattedMessage {...actionsRolesAllow.viewPayouts} />,
  ],
};

export const CorporateCardRoleDescriptions = () => {
  return (
    <RoleDescriptionsContainer
      roleDescriptions={[corporateCardRoleDescriptionProps]}
    />
  );
};

export const allCorporateCardRoleProps = [corporateCardRoleDescriptionProps];
